import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { getUserFromToken, useAuth } from './useAuth';

interface RoleContextType {
  role: string | null;
  userId: string | null;
  isRoleFetching: boolean;
  isRoleFetched: boolean;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

export const useRoleContext = (): RoleContextType => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRoleContext must be used within a RoleProvider');
  }
  return context;
};

interface RoleProviderProps {
  children: React.ReactNode;
}

export const RoleProvider = ({ children }: RoleProviderProps) => {
  const [role, setRole] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [isRoleFetching, setRoleFetching] = useState<boolean>(false);
  const [isRoleFetched, setRoleFetched] = useState<boolean>(false);

  const { logout } = useAuth();

  useEffect(() => {
    setRoleFetching(true);

    const decodedUser = getUserFromToken();

    if (decodedUser) {
      setRole(decodedUser.role ?? null);
      setUserId(decodedUser.id ?? null);
      setRoleFetched(true);
    } else {
      setRole(null);
      setUserId(null);
      setRoleFetched(false);
      logout();
    }

    setRoleFetching(false);
  }, [logout]);

  const contextValue = useMemo(
    () => ({
      role,
      userId,
      isRoleFetching,
      isRoleFetched
    }),
    [role, userId, isRoleFetching, isRoleFetched]
  );

  return (
    <RoleContext.Provider value={contextValue}>
      {children}
    </RoleContext.Provider>
  );
};
